import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components/macro'
import GrowthEngineNudgeCard, {
  getNudgeCardPropsFromRecommendation,
} from 'src/components/growthEngine/GrowthEngineNudgeCard'
import { useOllieAssistant } from 'src/components/ollie/OllieAssistantContext'
import useOllieFindings from 'src/hooks/useOllieFindings'
import {
  fetchGrowthEngineSuccessHub,
  postGrowthEngineRecommendationInteraction,
} from 'src/utils/growthEngineApi'
import { resolveGrowthEngineCtaPath } from 'src/utils/growthEngineCtaRoutes'
import { GROWTH_ENGINE_SUCCESS_HUB_SURFACE } from 'src/utils/growthEngineSuccessHub'
import { OLLIE_FINDING_SURFACES } from 'src/utils/ollieFindingsApi'
import {
  buildSuccessHubOlliePick,
  isSuccessHubOlliePickAction,
  SUCCESS_HUB_OLLIE_PICK_CTA_MODES,
} from 'src/utils/successHubOllie'

/** Today’s kitchen job, the same recommendation the app shows on Home. */
const VenueHomeOllieJob = ({ venueId, currentPlan, onJobText }) => {
  const history = useHistory()
  const { openOllieWithFinding } = useOllieAssistant()
  const { markClicked: markOllieClicked } = useOllieFindings({
    autoLoad: false,
  })
  const [hub, setHub] = useState(null)
  const [status, setStatus] = useState('loading')

  useEffect(() => {
    if (!venueId) return undefined
    let cancelled = false
    setStatus('loading')
    fetchGrowthEngineSuccessHub(venueId, { platform: 'dashboard' })
      .then(async next => {
        if (cancelled) return null
        setHub(next)
        setStatus('ready')
        const today = next?.today
        if (!today?.intentId) return null
        const pickFingerprint =
          next?.hero?.olliePick?.fingerprint ||
          today.olliePick?.fingerprint ||
          null
        try {
          await postGrowthEngineRecommendationInteraction(venueId, {
            intentId: today.intentId,
            interaction: 'shown',
            featureId: today.featureId,
            platform: 'dashboard',
            metadata: {
              surface: GROWTH_ENGINE_SUCCESS_HUB_SURFACE,
              homeHero: true,
              ...(pickFingerprint ? { fingerprint: pickFingerprint } : {}),
            },
          })
        } catch (_error) {
          return null
        }
        return null
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
        return null
      })
    return () => {
      cancelled = true
    }
  }, [venueId])

  useEffect(() => {
    if (status !== 'ready') {
      onJobText?.('')
      return
    }
    const today = hub?.today || hub?.hero || null
    const card = getNudgeCardPropsFromRecommendation(today, { currentPlan })
    onJobText?.(
      [card?.title, card?.body]
        .map(part => String(part || '').trim())
        .filter(Boolean)
        .join(' '),
    )
  }, [hub, status, currentPlan, onJobText])

  if (status === 'loading') return null
  if (status === 'error') {
    return <Quiet>Couldn’t load today’s recommendation.</Quiet>
  }

  const today = hub?.today || hub?.hero || null
  const card = getNudgeCardPropsFromRecommendation(today, { currentPlan })
  if (!card) {
    return <Quiet>You’re caught up on today’s kitchen job.</Quiet>
  }

  const action = today?.cta?.action
  const olliePick = buildSuccessHubOlliePick(today, { venueId })
  const recordClick = () => {
    if (!today?.intentId) return
    const pickFingerprint =
      today.olliePick?.fingerprint || olliePick?.fingerprint || null
    postGrowthEngineRecommendationInteraction(venueId, {
      intentId: today.intentId,
      interaction: 'clicked',
      featureId: today.featureId,
      platform: 'dashboard',
      metadata: {
        surface: GROWTH_ENGINE_SUCCESS_HUB_SURFACE,
        homeHero: true,
        ...(pickFingerprint ? { fingerprint: pickFingerprint } : {}),
      },
    }).catch(() => {})
  }

  const openOlliePick = () => {
    if (!olliePick) return
    markOllieClicked(olliePick.finding, OLLIE_FINDING_SURFACES.SUCCESS_HUB)
    if (olliePick.cta.mode === SUCCESS_HUB_OLLIE_PICK_CTA_MODES.NAVIGATE) {
      history.push(olliePick.cta.path)
      return
    }
    openOllieWithFinding(olliePick.checkId, {
      startAction: olliePick.cta.action,
    })
  }

  const openLadderCta = () => {
    const path = resolveGrowthEngineCtaPath(venueId, action, {
      fromProgress: true,
      featureId: today.featureId,
    })
    if (path) history.push(path)
  }

  const canOpenOlliePick =
    Boolean(olliePick) && isSuccessHubOlliePickAction(action)
  const canOpenLadder = Boolean(card.ctaLabel && action)

  return (
    <GrowthEngineNudgeCard
      {...card}
      showHumanPath={false}
      footerExtra={<OllieMark>Ollie recommends</OllieMark>}
      data-testid='venue-home-ollie-job'
      onCta={
        canOpenOlliePick
          ? () => {
              openOlliePick()
              recordClick()
            }
          : canOpenLadder
          ? () => {
              openLadderCta()
              recordClick()
            }
          : undefined
      }
    />
  )
}

const OllieMark = styled.span`
  margin-left: auto;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 0.8rem;
  font-weight: 600;
`

const Quiet = styled.p`
  margin: 0;
  padding: 0.9rem 1rem;
  border-radius: 16px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.oliveGrey};
`

export default VenueHomeOllieJob
